import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1758448501002-8c7bb7a7d9ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBzaG9wcGluZyUyMG1hbGwlMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzc3MjcwMjh8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100vh] pt-[68px] overflow-hidden bg-rt-bg"
        >
            {/* Background image with heavy overlay */}
            <div className="absolute inset-0 -z-10">
                <img
                    src={HERO_IMG}
                    alt="Modern retail interior"
                    className="w-full h-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-rt-bg/70 via-rt-bg/85 to-rt-bg" />
                <div className="absolute inset-0 rt-grid-bg rt-radial-fade opacity-60" />
            </div>

            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-9">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="rt-mono-label mb-8"
                            data-testid="hero-eyebrow"
                        >
                            <span className="inline-block w-2 h-2 bg-rt-teal mr-3 align-middle" />
                            Enterprise Smart Retail Platform
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.05 }}
                            className="rt-h1 text-[44px] sm:text-6xl lg:text-[88px] xl:text-[100px] text-white"
                            data-testid="hero-title"
                        >
                            The future of <span className="italic font-light text-rt-teal">in-store retail</span>
                            <br />
                            is already shopping.
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mt-8 max-w-2xl text-base lg:text-lg text-rt-text2 leading-relaxed"
                            data-testid="hero-description"
                        >
                            ReTechler is a full-stack smart retail platform — hardware, AI and SaaS
                            in one unified system. SmartCaddy, our AI-powered shopping cart family
                            (Caddy, Caddy Pro, Caddy Vision and Cobot), is our first product line
                            currently in test and development.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.35 }}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <a
                                href="#contact"
                                data-testid="hero-cta-demo"
                                className="inline-flex items-center gap-2 bg-rt-teal text-black font-medium px-6 py-3.5 hover:bg-rt-tealDim transition-colors"
                            >
                                Request a demo <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href="https://smartcaddy.store/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-smartcaddy"
                                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 hover:bg-white/5 transition-colors"
                            >
                                <Play className="w-4 h-4" /> Visit SmartCaddy
                            </a>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.5 }}
                        className="lg:col-span-3 lg:pl-8 lg:border-l lg:border-white/10 space-y-6"
                    >
                        <div>
                            <div className="rt-mono-label mb-2">Modular</div>
                            <div className="font-heading text-2xl text-white leading-tight">
                                Start with one module.
                            </div>
                            <div className="text-sm text-rt-text2 mt-1">Scale across the store when ready.</div>
                        </div>
                        <div>
                            <div className="rt-mono-label mb-2">Real-time</div>
                            <div className="font-heading text-2xl text-white leading-tight">
                                One unified data fabric.
                            </div>
                            <div className="text-sm text-rt-text2 mt-1">Every system speaks the same language.</div>
                        </div>
                        <div>
                            <div className="rt-mono-label mb-2">Open</div>
                            <div className="font-heading text-2xl text-white leading-tight">
                                API-first by default.
                            </div>
                            <div className="text-sm text-rt-text2 mt-1">Plug into your POS, ERP and loyalty stack.</div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
